import { addDoc, collection } from "firebase/firestore";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { Close } from "@mui/icons-material";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { firestore, storage } from "../../..";
import { useFeedback } from "../../../providers/feedback/feedback";
import { EditorialCommitteeMember } from "../../yuva_gaaz/yuvagaaz_data";
import { getDocs } from "firebase/firestore";
import { toTitleCaseName } from "../../../utils/title_case";

interface UploadEditorialMemberTabProps {
  onSuccess?: () => void;
}

const POST_OPTIONS = ["Director", "Editor", "Co-Editor", "Member"];

function normalizeUrl(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) return "";
  if (trimmedValue.startsWith("http://") || trimmedValue.startsWith("https://") || trimmedValue.startsWith("/")) {
    return trimmedValue;
  }
  return `https://${trimmedValue}`;
}

export default function UploadEditorialMemberTab({ onSuccess }: UploadEditorialMemberTabProps) {
  const { showSnackbar } = useFeedback();
  const [name, setName] = useState("");
  const [post, setPost] = useState("Member");
  const [place, setPlace] = useState("");
  const [img, setImg] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const submitLockRef = useRef(false);

  async function handleSubmit() {
    if (submitLockRef.current) {
      return;
    }

    if (!name.trim() || !post.trim()) {
      showSnackbar("Please fill member name and role");
      return;
    }

    try {
      submitLockRef.current = true;
      setIsSaving(true);
      const timestamp = new Date().toISOString();
      let finalImage = img.trim();

      if (selectedImage) {
        const storageRef = ref(storage, `yuvagaaz/editorial-committee/${Date.now()}-${selectedImage.name}`);
        await uploadBytes(storageRef, selectedImage);
        finalImage = await getDownloadURL(storageRef);
      }

      const existingMembersSnapshot = await getDocs(collection(firestore, "yuvagaaz_editorial_committee"));
      const nextOrder =
        existingMembersSnapshot.docs.reduce((maxOrder, memberDoc) => {
          const memberData = memberDoc.data() as EditorialCommitteeMember;
          return Math.max(maxOrder, memberData.order || 0);
        }, 0) + 1;

      await addDoc(collection(firestore, "yuvagaaz_editorial_committee"), {
        name: toTitleCaseName(name),
        post: post.trim(),
        place: place.trim(),
        img: normalizeUrl(finalImage),
        order: nextOrder,
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      setName("");
      setPost("Member");
      setPlace("");
      setImg("");
      setSelectedImage(null);
      showSnackbar("Committee member added successfully");
      onSuccess?.();
    } catch (error) {
      console.error("Error adding committee member", error);
      showSnackbar("Failed to add committee member");
    } finally {
      setIsSaving(false);
      submitLockRef.current = false;
    }
  }

  return (
    <Box mt={3}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Member Name"
            value={name}
            onChange={(event) => setName(toTitleCaseName(event.target.value))}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField select fullWidth label="Role" value={post} onChange={(event) => setPost(event.target.value)}>
            {POST_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12}>
          <TextField fullWidth label="Place" value={place} onChange={(event) => setPlace(event.target.value)} />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Image URL"
            value={img}
            onChange={(event) => {
              setImg(event.target.value);
              if (event.target.value) setSelectedImage(null);
            }}
            helperText="Use either an image URL or upload an image below."
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel>Upload Member Image</FormLabel>
            <input type="file" accept="image/*" style={{ border: "1px solid rgb(181, 176, 176)", width: "96%", paddingLeft: 8, paddingRight: 8, paddingTop: 16, paddingBottom: 16, borderRadius: 5, marginTop: 8 }} onChange={(event) => { if (event.target.files?.[0]) { setSelectedImage(event.target.files[0]); setImg(""); } }} />
            {selectedImage ? (
              <Box mt={2} position="relative" display="inline-block">
                <img src={URL.createObjectURL(selectedImage)} alt="Committee member preview" style={{ maxWidth: "220px", maxHeight: "220px", borderRadius: 8 }} />
                <IconButton size="small" onClick={() => setSelectedImage(null)} style={{ position: "absolute", top: 0, right: 0, color: "#ffffff", backgroundColor: "rgba(0,0,0,0.5)" }}>
                  <Close />
                </IconButton>
              </Box>
            ) : null}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Committee Member"}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
